import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { _isEmpty, _notNil } from '@/littledash.ts';

/**
 * Helper function to get filter operation key from operation name (same logic as APITable.utils.ts)
 */
const getFilterOperation = (name: string) =>
  Object.keys(filterType).find((key) => filterType[key as keyof typeof filterType] === name);

/**
 * Creates a custom filter processor that applies single URL encoding to multi-select field values
 * when using "contains" or "not_contains" operations. The API layer will apply a second encoding
 * to achieve the desired double encoding in the final REST call.
 * Backend stores multi-select values as encoded values.
 *
 * @param metadata - Array of metadata fields to identify multi-select types
 * @returns Custom filter processor function for use with APITable
 */
export const createMultiSelectDoubleEncodingProcessor = (
  metadata: Array<{ id: number | string; field_type: string }>
) => {
  const multiSelectFieldIds = new Set(
    metadata.filter((field) => field.field_type === 'multi_select').map((field) => field.id.toString())
  );

  return (filters: any[], params: URLSearchParams): void => {
    if (_isEmpty(multiSelectFieldIds)) {
      return;
    }

    filters.forEach((filter) => {
      const categoryValue = filter.category?.value?.toString();
      const operationName = filter.operation?.name;

      // Only process multi-select fields with contains/not_contains operations
      const isMultiSelectField = _notNil(categoryValue) && multiSelectFieldIds.has(categoryValue);
      const isContainsOperation = operationName === filterType.contains || operationName === filterType.not_contains;

      if (!isMultiSelectField || !isContainsOperation) {
        return;
      }

      encodeFilterParameter(params, categoryValue, operationName);
    });
  };
};

function encodeFilterParameter(params: URLSearchParams, categoryValue: string, operationName: string): void {
  const operationKey = getFilterOperation(operationName);
  if (operationKey === undefined) {
    return;
  }

  const filterParams = params.getAll('filters[]');
  const filterPrefix = `${categoryValue}|${operationKey}|`;
  const filterIndex = filterParams.findIndex((param) => param.startsWith(filterPrefix));

  if (filterIndex === -1) {
    return;
  }

  // Replace all filter parameters with the encoded version
  params.delete('filters[]');
  filterParams.forEach((param, index) => {
    if (index === filterIndex) {
      const [category, operation, value] = param.split('|');
      const encodedValue = encodeURIComponent(value);
      params.append('filters[]', `${category}|${operation}|${encodedValue}`);
    } else {
      params.append('filters[]', param);
    }
  });
}

/**
 * Creates a custom filter processor that converts interval filter operations to their API equivalents.
 * Converts: eq_interval -> eq, gt_interval -> gt, lt_interval -> lt, between_interval -> between
 *
 * @returns Custom filter processor function for use with APITable
 */
export const createIntervalFilterProcessor = () => {
  const intervalOperationMap: Record<string, string> = {
    eq_interval: 'eq',
    gt_interval: 'gt',
    lt_interval: 'lt',
    between_interval: 'between',
  };

  return (_filters: any[], params: URLSearchParams): void => {
    const filterParams = params.getAll('filters[]');
    const updatedFilters: Array<string> = [];

    filterParams.forEach((param) => {
      const [category, operation, value] = param.split('|');

      // Check if this is a timepoint filter with an interval operation
      const apiOperation = intervalOperationMap[operation];
      if (category === 'timepoint' && apiOperation !== undefined) {
        updatedFilters.push(`${category}|${apiOperation}|${value}`);
      } else {
        updatedFilters.push(param);
      }
    });

    // Replace all filter parameters with the updated versions
    params.delete('filters[]');
    updatedFilters.forEach((param) => {
      params.append('filters[]', param);
    });
  };
};

/**
 * Composes multiple filter processors into a single processor
 *
 * @param processors - Array of filter processor functions to apply in sequence
 * @returns Combined filter processor function
 */
export const composeFilterProcessors = (...processors: Array<(filters: any[], params: URLSearchParams) => void>) => {
  return (filters: any[], params: URLSearchParams): void => {
    processors.forEach((processor) => {
      processor(filters, params);
    });
  };
};
